import {
    ContributionState,
    ContributionMisc,
    PersonalContributionRequest,
    PlanDetails,
    FetchState,
    PlanType,
    FetchStateKey,
    OtherHsaAmountModel
} from "@/shared/types";
import * as PayrollService from "@/shared/api/payrollService";
import * as storeHelper from "@/shared/storeHelper";
import * as BankService from "@/shared/api/bankService";
import * as FileContributionService from "@/shared/api/fileContributionService";
import {
    computePerPayPeriod,
    computeEarlyPayDate
} from "../onboardingContributionHelper";
import * as _ from "lodash";
import * as UserService from "@/shared/api/userService";
import moment from "moment";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { formatCurrency } from "@/shared/currency";
import { errorIsStatus } from "@/shared/errorHelper";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useHsaPendingTransactionStore } from "@/shared/store/hsaPendingTransaction";
import { useHsaStore } from "@/shared/store/hsa";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { useUserPlanStore } from "@/employee/store/userPlan";

const emptyState: ContributionState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    updateRecurringEmployeeContributionFetchState: FetchState.Loaded,
    allowPriorYearContribution: false,
    allowPriorFetchState: FetchState.Uninitialized,
    otherHsaPriorYearCutoffDate: null,
    otherHsaPriorYearCutoffDateFetchState: FetchState.Uninitialized,
    planDetails: {
        planType: PlanType.Individual,
        effectiveDate: moment().format("YYYY-MM-DD"),
        otherHsaAmount: 0,
        enrollHsa: true,
        healthPlanEffectiveDate: null
    } as PlanDetails,
    planDetailsFetchState: FetchState.Uninitialized,
    contribution: {
        annualPreTaxContributionAmount: 0,
        annualPostTaxContributionAmount: 0,
        otherHsaContributionAmount: 0,
        perPayPeriodAmount: 0,
        coverageType: "",
        payrollCycle: "",
        noOfPayPeriods: 0,
        upcomingPayDates: [],
        employerContributionAmount: 0,
        planContributionLimit: 0,
        hsaEffectiveDate: null,
        isEnrollmentInDecember: false,
        planContributionLimitForDecEnrollment: 0,
        nextEffectivePayrollDate: null,
        employerContributionAmountForDecEnrollment: 0,
        earlyDepositCompleteDate: null
    }
};

function employeeContributionLimit (state: ContributionState): number {
    if (state.contribution.isEnrollmentInDecember) {
        const yearLimit = Math.max(state.contribution.planContributionLimit -
            (state.contribution.otherHsaContributionAmount || 0), 0);
        const maxAllowed = state.contribution.planContributionLimitForDecEnrollment;
        const employerContributionAmount = Math.min(state.contribution.isEnrollmentInDecember &&
        (state.contribution.otherHsaContributionAmount || 0) === 0
            ? state.contribution.employerContributionAmountForDecEnrollment
            : state.contribution.employerContributionAmount, Math.max(state.contribution.planContributionLimit, 0));
        if (yearLimit >= maxAllowed) {
            return _.round(Math.max(maxAllowed - employerContributionAmount, 0), 2);
        }
        return _.round(Math.max(yearLimit - employerContributionAmount, 0), 2);
    }
    return _.round(Math.max(state.contribution.planContributionLimit - state.contribution.employerContributionAmount
        - (state.contribution.otherHsaContributionAmount || 0), 0), 2);
}

function employerContributionAmount (state: ContributionState): number {
    if (state.contribution.isEnrollmentInDecember && (state.contribution.otherHsaContributionAmount || 0) === 0) {
        return state.contribution.employerContributionAmountForDecEnrollment;
    }

    return state.contribution.employerContributionAmount;
}

export const useContributionStore = defineStore("contribution", {
    state: (): ContributionState => (_.cloneDeep(emptyState)),
    getters: {
        annualMaxValue: state => employeeContributionLimit(state),
        isFirstEnrollmentInDec: state => state.contribution.isEnrollmentInDecember &&
            (state.contribution.otherHsaContributionAmount || 0) === 0,
        maxAllowedContribution: state => state.contribution.isEnrollmentInDecember &&
        (state.contribution.otherHsaContributionAmount || 0) === 0
            ? state.contribution.planContributionLimitForDecEnrollment : state.contribution.planContributionLimit,
        employerContributionForPlanYear: state => employerContributionAmount(state),
        userCoverageType: state => state.contribution.coverageType,
        hasNoPayPeriods: state => state.contribution.upcomingPayDates.length === 0,
        noOfPayPeriods: state => state.contribution.upcomingPayDates.length,
        userPayCycle: state => state.contribution.payrollCycle,
        effectiveDate: state => state.contribution.hsaEffectiveDate,
        contributionState: state => ({ ...state.contribution }),
        earlyDepositCompleteDate: state => state.contribution.earlyDepositCompleteDate,
        perPayPeriodState: state => state.contribution.perPayPeriodAmount,
        recurringContributionAnnualLimit () {
            return this.maxAllowedContribution /* I believe this is the IRS limit based on the plan type, age, etc */
                - this.contribution.otherHsaContributionAmount;
        },
        earlyDepositDate () {
            return computeEarlyPayDate({
                amountLimit: this.annualMaxValue,
                sortedPayDates: this.contribution.upcomingPayDates,
                perPayPeriodAmount: this.contribution.perPayPeriodAmount
            });
        },
        firstPlanEffectiveYear: state => moment(state.planDetails.effectiveDate).year(),
        otherHsaPriorYearCutoffDateLoaded: state => state.otherHsaPriorYearCutoffDateFetchState === FetchState.Loaded
    },
    actions: {
        setOtherHsaContributionAmount (otherAmount: number): void {
            if (otherAmount < 0) {
                return;
            }

            if (otherAmount > this.contribution.planContributionLimit) {
                otherAmount = this.contribution.planContributionLimit;
            }

            this.contribution.otherHsaContributionAmount = _.round(otherAmount, 2);

            if (this.contribution.isEnrollmentInDecember) {
                if (+(otherAmount || 0) === 0) {
                    this.contribution.planContributionLimitForDecEnrollment =
                        _.round(this.contribution.planContributionLimit / 12, 2);
                } else {
                    this.contribution.planContributionLimitForDecEnrollment = this.contribution.planContributionLimit;
                }
            }
        },
        setContribution (contribution: ContributionMisc): void {
            this.contribution = contribution;
        },
        setFetchState (newState: FetchState): void {
            this.fetchState = newState;
        },
        setUpdateRecurringEmployeeContributionFetchState (newState: FetchState): void {
            this.updateRecurringEmployeeContributionFetchState = newState;
        },
        setContributionAmounts (namedArgs:
                                    {
                                        annualPreTaxContributionAmount: number,
                                        perPayPeriodAmount: number
                                    }) {
            this.contribution.perPayPeriodAmount = namedArgs.perPayPeriodAmount;
            this.contribution.annualPreTaxContributionAmount = namedArgs.annualPreTaxContributionAmount;
        },
        setPriorYearContributionAllowed (isPriorYearContributionAllowed: boolean) {
            this.allowPriorYearContribution = isPriorYearContributionAllowed;
        },
        setPlanDetails (data: PlanDetails) {
            this.planDetails = data || {
                planType: PlanType.Individual,
                effectiveDate: null,
                otherHsaAmount: 0,
                enrollHsa: true
            };
        },
        setPlanDetailsFetchState ({ fetchState, fetchKey }: { fetchState: FetchState, fetchKey: FetchStateKey }) {

            if (this.fetchState === FetchState.Uninitialized) {
                this.fetchKey = fetchKey;
                this.planDetailsFetchState = fetchState;
                return;
            }
            if (fetchStateKeyMatch(this.fetchKey, fetchKey)) {
                this.planDetailsFetchState = fetchState;
            }
        },
        invalidateState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        setOtherHsaPriorYearCutoffDate (date: Date) {
            this.otherHsaPriorYearCutoffDate = date;
        },
        setOtherHsaPriorYearCutoffDateFetchState (fetchState: FetchState) {
            this.otherHsaPriorYearCutoffDateFetchState = fetchState;
        },
        fetchContribution (): Promise<any> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            const tryGetUserContribution = resolve => {
                return PayrollService.getUserContribution()
                    .then(response => {
                        const stateData = <ContributionMisc>response.data;
                        // a null perPayPeriodAmount here indicates
                        // this user has not updated the payroll service
                        // with their preferred contribution amount. use the value from the user service
                        if (stateData.perPayPeriodAmount === null) {
                            return UserService.getInitialContribution()
                                .then(userServiceResponse => {
                                    stateData.annualPreTaxContributionAmount =
                                        Math.min((<number>userServiceResponse.data) || 0,
                                            (stateData.planContributionLimitForDecEnrollment -
                                                stateData.employerContributionAmountForDecEnrollment));
                                    return stateData;
                                });
                        }
                        return stateData;
                    })
                    .then((stateData: ContributionMisc) => {
                        if (stateData.annualPreTaxContributionAmount === null) {
                            stateData.annualPreTaxContributionAmount = 0;
                        }
                        stateData.perPayPeriodAmount = computePerPayPeriod({
                            totalAmount: stateData.annualPreTaxContributionAmount,
                            numberOfPeriods: stateData.upcomingPayDates.length
                        });

                        const hsaLimitStore = useHsaLimitStore();
                        this.setContribution(stateData);
                        this.setOtherHsaContributionAmount(hsaLimitStore.otherHsaAmount() || 0);
                        this.setFetchState(FetchState.Loaded);
                    });
            };

            return new Promise<void>((resolve, reject) => {
                let action: () => void;
                action = () => {
                    tryGetUserContribution(() => {
                        resolve();
                    }).catch(err => {
                        if (err && err.response && err.response.status === 404) {
                            // user not there yet, keep trying
                            window.setTimeout(action, 5000);
                            return;
                        }

                        this.setFetchState(FetchState.Uninitialized);
                        const notifierStore = useNotifierStore();
                        this.notifierStore.setDanger("Could not load the employee contribution amount");
                        reject(err);
                    });
                };

                action();
            });
        },
        updateContribution (): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                const otherHsa: OtherHsaAmountModel = {
                    amount: this.contribution.otherHsaContributionAmount,
                    year: moment().year()
                };
                UserService.saveOtherHsaAmount(otherHsa)
                    .then(() => {
                        return PayrollService.updateUserContribution(this.contribution);
                    })
                    .then(response => {
                        const hsaLimitStore = useHsaLimitStore();
                        hsaLimitStore.setOtherHsaAmount(this.contribution.otherHsaContributionAmount);
                        this.setContribution(response.data);
                        this.setOtherHsaContributionAmount(hsaLimitStore.otherHsaAmount() || 0);
                        resolve();
                    })
                    .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
            });
        },
        updatePerPayPeriodState (perPayPeriodArg: number) {
            const perPayPeriod = _.round(perPayPeriodArg, 2);
            // round added below to correct for floating point issues
            // example issue was 99.99 * 6 = 599.9399999999999
            const annualAmount = _.min(
                [
                    _.round(perPayPeriod * this.noOfPayPeriods, 6),
                    this.annualMaxValue
                ]);

            this.setContributionAmounts(
                { annualPreTaxContributionAmount: annualAmount, perPayPeriodAmount: perPayPeriod });
        },
        updateAnnualState (annual: number) {
            const annualPreTaxContributionAmount = annual;
            let perPayPeriodAmount = this.contribution.perPayPeriodAmount;
            if (this.annualMaxValue >= annual) {
                perPayPeriodAmount = computePerPayPeriod({
                    totalAmount: annual,
                    numberOfPeriods: this.noOfPayPeriods
                });
            }
            this.setContributionAmounts(
                {
                    annualPreTaxContributionAmount,
                    perPayPeriodAmount
                });
        },
        processPersonalContribution (request: PersonalContributionRequest) {
            return BankService.personalContributionRequest(request)
                .then(_ => {
                    const currentYear = moment().year();
                    const year = request.isPriorYear ? (currentYear - 1) : currentYear;
                    const hsaStore = useHsaStore();
                    const hsaTotal = useHsaTotalStore();
                    const hsaPendingTransactionStore = useHsaPendingTransactionStore();
                    const notifierStore = useNotifierStore();

                    hsaTotal.reload(null);
                    hsaStore.reload(year);
                    hsaPendingTransactionStore.fetchContributionActivity(null);
                    const formattedContribution = formatCurrency(request.amount);
                    notifierStore.setSuccess(
                        `Your contribution for ${formattedContribution} has been successfully submitted`);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    return Promise.resolve();
                });
        },
        getOtherHsaPriorYearCutoffDate () {
            if (this.otherHsaPriorYearCutoffDateFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setOtherHsaPriorYearCutoffDateFetchState(FetchState.Loading);
            return PayrollService.getOtherHsaCutOffDate()
                .then(result => {
                    this.setOtherHsaPriorYearCutoffDate(result.data);
                    this.setOtherHsaPriorYearCutoffDateFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setOtherHsaPriorYearCutoffDateFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        getPriorYearContributionEligibilityStatus () {
            return BankService.getPriorYearContributionEligibility()
                .then(result => {
                    this.setPriorYearContributionAllowed(result.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        savePlanDetails (details: PlanDetails): Promise<any> {
            const userPlanStore = useUserPlanStore();
            userPlanStore.setUserPlanType(details.planType);
            return UserService.savePlanDetails(details)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        getPlanDetails (args?: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            args = args || {};

            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateState(fetchKey);

            if (this.planDetailsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setPlanDetailsFetchState({ fetchKey, fetchState: FetchState.Loading });

            return UserService.getPlanDetails(args.username, args.partnerId).then(result => {
                this.setPlanDetails(result.data);
                this.setPlanDetailsFetchState({ fetchKey, fetchState: FetchState.Loaded });
            }).catch(err => {
                if (errorIsStatus(err, 404)) {
                    this.setPlanDetailsFetchState({ fetchKey, fetchState: FetchState.Loaded });
                } else {
                    return storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        }
    }
});